<script setup>
// @ts-check

import { previewPayslipInput } from '@/api/payslipInputs'
import { getPayslip } from '@/api/payslips'
import SummaryContainer from '@/components/containers/summary/SummaryContainer.vue'
import PayslipItemSummary from '@/components/salaries/summaries/PayslipItemSummary.vue'
import { usePayslipInputActions } from '@/core/composables/salaries/usePayslipInputActions'
import { reformatDateString } from '@/helpers/dates'
import {
  SALARY_DETAILS,
  SALARY_EMPLOYEE,
  SALARY_TAX_CARD
} from '@/router/salaryRoutes'
import { useLanguageStore } from '@/store/language'
import { useArrayFind } from '@vueuse/core'
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { onActivated, onMounted, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import PayslipInputRecipientSummary from './summaries/PayslipInputRecipientSummary.vue'
import PayslipInputTaxCardSummary from './summaries/PayslipInputTaxCardSummary.vue'

const props = defineProps({
  payslipInput: {
    /** @type {import('vue').PropType<PayslipInput>} */
    type: Object,
    required: true
  },
  disabled: {
    type: Boolean
  },
  secured: {
    type: Boolean
  }
})

const emit = defineEmits(['complete'])

defineOptions({
  name: 'SalarySummary'
})

const languageStore = useLanguageStore()
const { language } = storeToRefs(languageStore)
const { t } = useI18n()
const route = useRoute()

const { payslipInput } = toRefs(props)

/** @type {import('vue').Ref<?PayslipItem[]>} */
const items = ref(null)
/** @type {import('vue').Ref<Payslip[]>} */
const payslips = ref([])
const isLoading = ref(true)

const { showPayslipInputApprovalDialog } = usePayslipInputActions({
  onUpdate: () => emit('complete')
})

const originalPayslip = useArrayFind(
  payslips,
  (payslip) => payslip.calculation_type === 'COMPANY_OWNER'
)

const reversePayslip = useArrayFind(
  payslips,
  (payslip) => payslip.calculation_type === 'REVERSE'
)

const fetchDetails = async () => {
  items.value = null
  payslips.value = []

  if (payslipInput.value.payslips.length) {
    payslips.value = await Promise.all(
      payslipInput.value.payslips.map((payslipId) =>
        getPayslip(payslipId, language.value)
      )
    )
  } else {
    items.value = await previewPayslipInput(
      props.payslipInput.id,
      language.value
    )
  }
}

watch(language, fetchDetails)

onMounted(async () => {
  await fetchDetails()
  isLoading.value = false
})

onActivated(() => {
  if (isLoading.value) {
    return
  }
  fetchDetails()
})

const getRoute = (/** @type {any} */ routeName) =>
  props.disabled
    ? undefined
    : {
        name: routeName,
        params: _.set(route.params, 'id', props.payslipInput.id)
      }
</script>

<template>
  <component
    :is="disabled ? 'div' : 'form'"
    class="flex flex-col gap-y-5 sm:p-4"
    @submit.prevent="showPayslipInputApprovalDialog(payslipInput)"
  >
    <SummaryContainer
      :route="getRoute(SALARY_EMPLOYEE.name)"
      :header="t('salary.headings.salaried_person_details')"
    >
      <PayslipInputRecipientSummary :payslip-input :secured />
    </SummaryContainer>

    <SummaryContainer
      v-if="payslipInput.income_limit !== null"
      :header="t('salary.steps.salary_tax_card.label')"
      :route="getRoute(SALARY_TAX_CARD.name)"
    >
      <PayslipInputTaxCardSummary :payslip-input />
    </SummaryContainer>

    <SummaryContainer
      v-if="payslipInput.monetary_wage !== null"
      :header="t('salary.headings.salary_information')"
      :route="getRoute(SALARY_DETAILS.name)"
    >
      <div class="flex flex-col gap-y-3">
        <div class="flex flex-row justify-between gap-x-2">
          <span>{{ t('salary.labels.salary_period') }}</span>
          <span class="text-right">
            {{ reformatDateString(payslipInput.period_start) }}
            -
            {{ reformatDateString(payslipInput.period_end) }}
          </span>
        </div>

        <div v-if="!items && !payslips.length">
          <VSkeletonLoader v-for="i in 6" :key="i" type="list-item" />
        </div>

        <PayslipItemSummary v-if="items" :items />
        <PayslipItemSummary
          v-if="originalPayslip"
          :items="originalPayslip.items"
        />
      </div>
    </SummaryContainer>

    <SummaryContainer
      v-if="reversePayslip"
      :header="t('salary.invalidation_details')"
    >
      <div class="flex flex-col gap-y-3">
        <PayslipItemSummary :items="reversePayslip.items" />
      </div>
    </SummaryContainer>

    <slot name="controls" />
  </component>
</template>
